import { t, useUiLocale } from '@/i18n/locale';
import { useEffect, useRef, useState } from 'react';
import type { BuddyProfile } from './companionApi';
type Value = { value: number };
type Player = {
  cleanup(): void;
  resizeDrawingSurfaceToCanvas(): void;
  pause(name: string): void;
  play(name: string): void;
  viewModelInstance?: {
    number(name: string): Value | null;
    trigger(name: string): { trigger(): void } | null;
  };
};
type Runtime = {
  RuntimeLoader: { setWasmUrl(url: string): void };
  Rive: new (opts: Record<string, unknown>) => Player;
  Layout: new (opts: Record<string, unknown>) => unknown;
  Fit: { Contain: unknown };
  Alignment: { Center: unknown };
};
declare global {
  interface Window {
    rive?: Runtime;
  }
}
let runtimePromise: Promise<Runtime> | null = null;
function runtime() {
  if (window.rive) return Promise.resolve(window.rive);
  if (!runtimePromise)
    runtimePromise = new Promise<Runtime>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = '/media/companion/rive.js';
      script.onload = () => {
        if (window.rive) resolve(window.rive);
        else {
          runtimePromise = null;
          script.remove();
          reject(new Error('Character unavailable'));
        }
      };
      script.onerror = () => {
        script.remove();
        runtimePromise = null;
        reject(new Error('Character unavailable'));
      };
      document.head.append(script);
    });
  return runtimePromise;
}
const hats = {
  none: '',
  cap: 'capVisible',
  beanie: 'beanieVisible',
  crown: 'crownVisible',
  beret: 'beretVisible',
  sailor: 'sailorVisible',
  party: 'partyVisible',
};
export function BuddyAvatar({
  profile,
  paused = false,
  className = '',
}: {
  profile: BuddyProfile;
  paused?: boolean;
  className?: string;
}) {
  useUiLocale();
  const canvas = useRef<HTMLCanvasElement>(null),
    player = useRef<Player | null>(null),
    latest = useRef(profile);
  latest.current = profile;
  const latestPaused = useRef(paused);
  latestPaused.current = paused;
  const [failed, setFailed] = useState(false),
    [attempt, setAttempt] = useState(0);
  const apply = () => {
    const p = player.current,
      v = p?.viewModelInstance;
    if (!v) return;
    const profile = latest.current;
    for (const field of Object.values(hats)) {
      if (field) {
        const n = v.number(field);
        if (n) n.value = field === hats[profile.hat] ? 1 : 0;
      }
    }
    for (const key of ['satchel', 'scarf', 'backpack', 'bow', 'pendant', 'moonbag'] as const) {
      const n = v.number(key + 'Visible');
      if (n)
        n.value =
          profile.accessories.includes(key) &&
          !(profile.character === 'tuantuan' && key === 'scarf')
            ? 1
            : 0;
    }
    const ears = {
      leftUpright: profile.earStyle !== 'down',
      rightUpright: profile.earStyle === 'up',
      leftFloppy: profile.earStyle === 'down',
      rightFloppy: profile.earStyle !== 'up',
    };
    for (const [field, visible] of Object.entries(ears)) {
      const n = v.number(field);
      if (n) n.value = visible ? 1 : 0;
    }
  };
  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    void runtime()
      .then((r) => {
        if (cancelled || !canvas.current) return;
        r.RuntimeLoader.setWasmUrl('/media/companion/rive.wasm');
        const p: Player = new r.Rive({
          src: '/media/companion/plush-companion.riv',
          canvas: canvas.current,
          artboard: profile.character === 'tuantuan' ? 'TuanTuan' : 'Plush',
          stateMachine: 'Companion',
          autoBind: true,
          autoplay: !paused && !window.matchMedia('(prefers-reduced-motion: reduce)').matches,
          layout: new r.Layout({ fit: r.Fit.Contain, alignment: r.Alignment.Center }),
          onLoad: () => {
            if (cancelled) return;
            p.resizeDrawingSurfaceToCanvas();
            apply();
            if (latestPaused.current) p.pause('Companion');
            else p.viewModelInstance?.trigger('wave')?.trigger();
          },
          onLoadError: () => {
            if (!cancelled) setFailed(true);
          },
        });
        player.current = p;
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    const resize = () => player.current?.resizeDrawingSurfaceToCanvas();
    window.addEventListener('resize', resize);
    return () => {
      cancelled = true;
      window.removeEventListener('resize', resize);
      player.current?.cleanup();
      player.current = null;
    };
  }, [profile.character, attempt]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    apply();
  }, [profile]);
  useEffect(() => {
    if (paused) player.current?.pause('Companion');
    else player.current?.play('Companion');
  }, [paused]);
  return (
    <div className={'relative ' + className}>
      <canvas
        ref={canvas}
        width={480}
        height={560}
        className="h-full w-full object-contain"
        role="img"
        aria-label={profile.name + t(' the animated AI buddy')}
      />
      <button
        type="button"
        className="absolute inset-0 rounded-2xl focus-visible:ring-2 focus-visible:ring-primary-500"
        aria-label={t('Wave to ') + profile.name}
        disabled={paused || failed}
        onClick={() => player.current?.viewModelInstance?.trigger('wave')?.trigger()}
      />
      {failed && (
        <button
          className="btn-pill-ghost absolute inset-x-4 bottom-3"
          onClick={() => setAttempt((x) => x + 1)}
        >
          {' '}
          {t('Try character again')}{' '}
        </button>
      )}
    </div>
  );
}
